import { Component, inject, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { ActionGroup } from '../../../elements/components/action-group/entities/action-group.entity';
import { ActionItem } from '../../../elements/components/action-item/entities/action-item.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { Comparable } from '../../../helpers/entities/comparable.interface';
import { Trackable } from '../../../helpers/entities/trackable.interface';
import { RESULTS_PER_PAGE_OPTIONS } from '../../generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { SortOption } from '../../generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { PaginatedResults } from '../../generic-overview/components/pagination/entities/paginated-results.entity';
import { ColumnDef } from '../../list-view/entities/column-definition.entity';
import { SearchBarMode } from '../../list-view/entities/filter-bar-mode.entity';
import { SearchPageConfig, SearchPageFeatures } from '../entities/search-page-config.entity';
import { Panel, SearchState } from '../entities/search-page-state.entity';
import { SearchPageStateService } from '../state-machines/search-page-state.service';

@Component({
    standalone: true,
    selector: 'sh-search-page-impl',
    template: ''
})
export abstract class SearchPageImplContainerComponent<T extends Comparable & Trackable>
    extends BaseContainer
    implements OnInit
{
    protected readonly searchPageStateService: SearchPageStateService = inject(SearchPageStateService);

    public searchPageConfig: SearchPageConfig<T>;

    // Identifiers
    protected abstract readonly PAGE_ID: string;
    protected abstract readonly PAGE_TITLE: string;

    // SearchBar
    protected abstract readonly SEARCH_BAR_MODE: SearchBarMode;
    protected readonly FIELD_SEARCH_OPTIONS: MultiFormSelectOptions = {
        options: [],
        isLiveData: false
    };

    // Search
    protected readonly performRefresh$ = new BehaviorSubject<boolean>(true);

    // Columns
    protected abstract readonly COLUMNS: ColumnDef<any, T>[];
    protected readonly COLUMN_OPTIONS: FormSelectOption[] = [];

    // Sort
    protected abstract readonly DEFAULT_SORT_MODE: SortInfo;
    protected abstract readonly DEFAULT_SORT_OPTIONS: SortOption[];

    // Pagination
    protected readonly DEFAULT_PAGE_SIZE: number = 25;
    protected readonly PAGE_SIZE_OPTIONS = RESULTS_PER_PAGE_OPTIONS;

    // Features
    protected abstract readonly FEATURES: Record<SearchPageFeatures | Panel, boolean>;

    // Translations
    protected abstract ITEMS_SELECTED_LABEL_KEY: string;
    protected abstract NO_ITEMS_SELECTED_LABEL_KEY: string;
    protected abstract RESULTS_LABEL_KEY: string;
    protected abstract NO_RESULTS_LABEL_KEY: string;

    ngOnInit(): void {
        this.initSearchPageConfig();
    }

    private initSearchPageConfig(): void {
        this.searchPageConfig = {
            id: this.PAGE_ID,
            title: this.PAGE_TITLE,
            features: this.FEATURES,
            sort: {
                default: this.DEFAULT_SORT_MODE,
                options: this.DEFAULT_SORT_OPTIONS
            },
            pageSize: {
                default: this.DEFAULT_PAGE_SIZE,
                options: this.PAGE_SIZE_OPTIONS
            },
            search: {
                searchBarMode: this.SEARCH_BAR_MODE,
                fieldSearchOptions: this.FIELD_SEARCH_OPTIONS,
                refreshTriggers: this.getRefreshTriggers(),
                getSearchBarActionsFct: this.getSearchBarActions.bind(this),
                getSearchPageResultsFct: this.getSearchPageResults.bind(this),
                getSelectedItemsFct: this.getSelectedItems.bind(this),
                getCustomErrorMsgFct: this.getCustomLoadingErrorMessage.bind(this)
            },
            list: {
                columns$: of(this.COLUMNS),
                columnOptions$: of(this.COLUMN_OPTIONS)
            },
            actions: {
                getGlobalActionsFct: this.getGlobalActions.bind(this),
                getActionsFct: this.getActions.bind(this),
                getBulkActionsFct: this.getBulkActions.bind(this),
                getBatchActionsFct: this.getBatchActions.bind(this),
                onRowClickFct: this.onRowClick.bind(this)
            },
            translations: {
                itemsSelectedLabelKey: this.ITEMS_SELECTED_LABEL_KEY,
                noItemsSelectedLabelKey: this.NO_ITEMS_SELECTED_LABEL_KEY,
                resultsLabelKey: this.RESULTS_LABEL_KEY,
                noResultsLabelKey: this.NO_RESULTS_LABEL_KEY
            }
        };
    }

    // #region Results
    protected abstract getSearchPageResults(searchState: SearchState): Observable<PaginatedResults<T>>;

    protected abstract getSelectedItems(selectedItemIds: string[]): Observable<T[]>;

    protected getSearchBarActions(): Observable<ActionItem[]> {
        return of([]);
    }

    protected getCustomLoadingErrorMessage(error: Error): string | undefined {
        // Returning null means to display default 'Loading failed' message
        return undefined;
    }
    // #endregion

    // #region Actions
    protected getGlobalActions(): Observable<ActionItem[]> {
        return of([]);
    }

    protected abstract getActions(items: T[]): Observable<ActionGroup[]>;

    protected abstract getBulkActions(items: T[]): Observable<ActionItem[]>;

    protected getBatchActions(): Observable<ActionItem[]> {
        return of([]);
    }

    protected getRefreshTriggers(): Observable<boolean>[] {
        return [this.performRefresh$.asObservable()];
    }

    protected onRowClick(item: T): void {
        return;
    }

    protected triggerManualRefresh(): void {
        this.performRefresh$.next(true);
    }
    // #endregion
}
